import type { AttributeGroupListItem } from '@/entities/attributes';

/**
 * Формирует отображаемое значение комментария LLM для строки оценки.
 * Если атрибут является копией (`isCopy`), добавляет озвученное значение и реакцию клиента.
 * Затем объединяет причину отказа и комментарий LLM через разделитель ` | `.
 */
export const getLLMCommentValue = ({
  refusalReason,
  llmComment,
  announced,
  reaction,
  isCopy,
}: {
  refusalReason: string;
  llmComment: string;
  announced?: string;
  reaction?: string;
  isCopy?: boolean;
}) => {
  const additionalValues = [];

  if (isCopy) {
    additionalValues.push(announced ? `Озвучен: ${announced}` : '');
    additionalValues.push(reaction ? `Реакция клиента: ${reaction}` : '');
  }

  return additionalValues.filter(Boolean).join(' | ') + [refusalReason, llmComment].filter(Boolean).join(' | ');
};

/** Коды групп атрибутов, которые всегда идут первыми в фиксированном порядке. */
const PRIORITY_GROUP_CODES = ['system', 'ats', 'business', 'speechAnalytics'];

/**
 * Сортирует группы атрибутов: приоритетные группы (`system`, `ats`, `business`, `speechAnalytics`)
 * идут первыми в заданном порядке, остальные — по алфавиту.
 */
export const sortAttributeGroups = (groups: AttributeGroupListItem[]): AttributeGroupListItem[] => {
  const groupsByCode = new Map(groups.map((g) => [g.code, g]));
  const sorted: AttributeGroupListItem[] = [];
  for (const code of PRIORITY_GROUP_CODES) {
    const group = groupsByCode.get(code);
    if (group) {
      sorted.push(group);
      groupsByCode.delete(code);
    }
  }
  const remaining = Array.from(groupsByCode.values()).sort((a, b) => a.name.localeCompare(b.name));
  sorted.push(...remaining);
  return sorted;
};

/**
 * Конвертирует строку в kebab-case.
 * Заменяет подчёркивания на дефисы и вставляет дефис перед заглавными буквами.
 */
export const toKebabCase = (str: string): string =>
  str
    .replace(/_/g, '-')
    .replace(/([a-z\d])([A-Z])/g, '$1-$2')
    .toLowerCase();
